/**
 * Cheap checks on the RAW input, run before Zod parses a single cell.
 *
 * Parsing element by element is linear in what the model sent and raises an
 * issue per bad element: a 4 MB `setValues` of a million rows once produced a
 * million issues, a 170 MB message and ~0.8 s of blocked event loop. These
 * checks bound every grid by the batch's cell budget first, stop at the first
 * bad row, and report one bad cell plus a count, so a refusal costs at most
 * O(ops + MAX_TOUCHED_CELLS_PER_BATCH) and carries one short message.
 */
import { z } from 'zod';
import { boundedRange, canonicalA1, rangeShape } from './ranges';
import { MAX_OPS_PER_BATCH, MAX_TOUCHED_CELLS_PER_BATCH as CAP, cellValueSchema, formulaSchema, } from './op-fields';
const fmt = (n) => n.toLocaleString('en-US');
const isRecord = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
/** Fatal, so the Zod parse after the preflight is skipped (the cost bound). */
const fail = (ctx, path, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path, message, fatal: true });
/** Bad cells counted exactly up to this many; past it the message says `99+`. */
const MAX_BAD_CELLS_COUNTED = 100;
/** Range area of an op, raw or parsed; 0 without a bounded range. */
export function rangeArea(op) {
    if (!isRecord(op) || typeof op.range !== 'string')
        return 0;
    const r = boundedRange(op.range);
    if (!r)
        return 0;
    const { rows, columns } = rangeShape(r);
    return rows * columns;
}
function gridOf(op) {
    if (!isRecord(op))
        return null;
    let grid = null;
    if (op.op === 'setValues' && Array.isArray(op.values)) {
        grid = { field: 'values', rows: op.values, cell: cellValueSchema };
    }
    else if (op.op === 'setFormulas' && Array.isArray(op.formulas)) {
        grid = { field: 'formulas', rows: op.formulas, cell: formulaSchema };
    }
    return grid;
}
/** Cells in a grid, counting a row as at least 1; stops once past `limit`. */
function gridCells(rows, limit) {
    let n = 0;
    for (const row of rows) {
        n += Array.isArray(row) ? Math.max(1, row.length) : 1;
        if (n > limit)
            break;
    }
    return n;
}
/** The first row whose shape does not match the range; false when all do. */
function badShape(g, range, ctx) {
    const r = boundedRange(range);
    if (!r)
        return false;
    const { rows, columns } = rangeShape(r);
    const a1 = canonicalA1(r);
    if (g.rows.length !== rows) {
        fail(ctx, [g.field], `${g.field} has ${g.rows.length} rows but range ${a1} has ${rows}; give one inner array per row`);
        return true;
    }
    for (let i = 0; i < g.rows.length; i++) {
        const row = g.rows[i];
        let message = null;
        if (!Array.isArray(row))
            message = `${g.field}[${i}] must be an array of cells`;
        else if (row.length !== columns) {
            message = `${g.field}[${i}] has ${row.length} cells but range ${a1} has ${columns} columns`;
        }
        if (message) {
            fail(ctx, [g.field, i], message);
            return true;
        }
    }
    return false;
}
// A number the value union refuses is NaN or Infinity, which Zod 4 reports
// only as invalid_union, so the finite message is chosen here.
const cellMessage = (value, issue) => {
    if (issue.code !== z.ZodIssueCode.invalid_union)
        return issue.message;
    return typeof value === 'number'
        ? 'must be a finite number'
        : 'must be text, a number, true/false or null';
};
/**
 * The first bad cell, with a count of the others. Counting stops at
 * MAX_BAD_CELLS_COUNTED failures: a failed parse is the expensive path.
 */
function badCells(g, ctx) {
    let first = null;
    let bad = 0;
    for (let i = 0; i < g.rows.length && bad < MAX_BAD_CELLS_COUNTED; i++) {
        const row = g.rows[i];
        if (!Array.isArray(row))
            continue;
        for (let j = 0; j < row.length && bad < MAX_BAD_CELLS_COUNTED; j++) {
            const cell = row[j];
            const r = g.cell.safeParse(cell);
            if (r.success)
                continue;
            bad += 1;
            first ??= { i, j, message: cellMessage(cell, r.error.issues[0]) };
        }
    }
    if (!first)
        return;
    const { i, j, message } = first;
    const others = bad >= MAX_BAD_CELLS_COUNTED ? `${MAX_BAD_CELLS_COUNTED - 1}+` : fmt(bad - 1);
    const more = bad > 1 ? ` (and ${others} more cells with problems)` : '';
    fail(ctx, [g.field, i, j], `${g.field}[${i}][${j}] ${message}${more}`);
}
/** Per op: bound the grid, then check its shape, then its cells. */
export function preflightOp(op, ctx) {
    const g = gridOf(op);
    if (!g)
        return;
    if (gridCells(g.rows, CAP) > CAP) {
        fail(ctx, [g.field], `${g.field} holds more than ${fmt(CAP)} cells, the most one batch may touch; split the change into several batches`);
        return;
    }
    const range = isRecord(op) && typeof op.range === 'string' ? op.range : null;
    if (range && badShape(g, range, ctx))
        return;
    badCells(g, ctx);
}
/**
 * Per batch, before any op is parsed: the op count, then the summed range
 * areas and grids. Input that is not an array is left to the Zod parse.
 */
export function preflightBatch(ops, ctx) {
    if (!Array.isArray(ops))
        return;
    if (ops.length < 1 || ops.length > MAX_OPS_PER_BATCH) {
        fail(ctx, [], `a batch holds 1 to ${MAX_OPS_PER_BATCH} ops; this one has ${ops.length}. Split larger changes into several batches`);
        return;
    }
    const area = ops.reduce((sum, op) => sum + rangeArea(op), 0);
    if (area > CAP) {
        fail(ctx, [], `${fmt(area)} cells touched; the limit is ${fmt(CAP)}. Split the change into smaller batches`);
        return;
    }
    let cells = 0;
    for (const op of ops) {
        const g = gridOf(op);
        if (g)
            cells += gridCells(g.rows, CAP - cells);
        if (cells > CAP) {
            fail(ctx, [], `values and formulas across the batch hold more than ${fmt(CAP)} cells; the limit is ${fmt(CAP)}. Split the change into smaller batches`);
            return;
        }
    }
}
//# sourceMappingURL=preflight.js.map